import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { RatingComponent } from '@df/ui/common/rating.component';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { PwaAppInstallationModalInitiatorService } from './pwa-app-installation-modal.initiator.service';

export namespace NPwaAppInstallationModal {
    export const MODAL_ID = 'pwa-app-installation-modal';
    export type IBlockData = Catalog.IBlockData<IData>;

    export interface IData {
        image: string;
        logo: string;
        reviewsNumber?: number;
        copy: string;
        buttonCopy: string;
        iosAppLink: string;
        androidAppLink: string;
        delay?: number; // seconds
    }
}

@DfCmsModal({
    id: NPwaAppInstallationModal.MODAL_ID,
    name: 'App Installation Modal',
    position: 'bottom',
    config: () => ({
        width: '100vw',
        height: 'auto',
        minWidth: '100%',
        modalClass: 'p-a-0 bg-col-w pos-relative',
        position: {
            left: '0',
            right: '0',
            bottom: '0'
        },
        scrollStrategy: 'noop',
        closeOnNavigation: true,
        hasBackdrop: false
    }),
    blockGroup: 'installationModal',
    priority: 0,
    initiators: [PwaAppInstallationModalInitiatorService],
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: () => ['S', 'M']
})
@ICmsComponent<PwaAppInstallationModalComponent>({
    id: NPwaAppInstallationModal.MODAL_ID,
    block: { url: NPwaAppInstallationModal.MODAL_ID },
    onDemandOnly: true,
    name: 'App Installation Modal',
    tags: ['app', 'install', 'modal'],
    fields: [
        <NICmsContentField.IField>{
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'logo',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo',
            required: true
        },
        <NICmsContentField.INumberField>{
            key: 'reviewsNumber',
            type: NICmsContentField.EFieldType.number,
            title: 'Reviews number',
            min: 0,
            max: 5
        },
        <NICmsContentField.IField>{
            key: 'copy',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Copy',
            default: 'Available in the App Store',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'buttonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Button copy',
            default: 'Get the app',
            required: true
        },
        <NICmsContentField.INumberField>{
            key: 'delay',
            type: NICmsContentField.EFieldType.number,
            title: 'Delay (seconds)',
            default: 0,
            min: 0,
            max: 60
        },
        <NICmsContentField.IField>{
            key: 'iosAppLink',
            type: NICmsContentField.EFieldType.text,
            title: 'iOS app link',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'androidAppLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Android app link',
            required: true
        }
    ]
})
@Component({
    selector: 'pwa-app-installation-modal',
    templateUrl: './pwa-app-installation-modal.html',
    styleUrls: ['pwa-app-installation-modal.scss'],
    standalone: true,
    imports: [ICmsClientModule, CommonModule, DfImageDirective, TranslatePipe, RatingComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PwaAppInstallationModalComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({ type: 'blockContent' })
    content?: NPwaAppInstallationModal.IData;

    constructor(
        private modalRef: ModalRef<NPwaAppInstallationModal.IData>,
        private platform: Platform,
        private app: App
    ) {
        super();
    }

    get isIos(): boolean {
        return this.app.isPlatformBrowser && this.platform.IOS;
    }

    get appLink(): string | undefined {
        const content = this.content;
        if (!content) {
            return undefined;
        }

        return this.isIos ? content.iosAppLink : content.androidAppLink;
    }

    protected close() {
        this.modalRef?.close();
    }
}
